/**
 * Permission codes the Workforce Structures screens (FSD 1.2.5) declare an
 * interest in.
 *
 * They are the dot-strings `server/internal/org/permissions_fnd.go` registers
 * with the RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me` —
 * confirmed directly against that file, not guessed:
 *
 *   PermNamingTemplateRead   = "org.naming_template.read"
 *   PermNamingTemplateManage = "org.naming_template.manage"
 *   PermHeadcountManage      = "org.headcount.manage"
 *   PermReorganisationRead   = "org.reorganisation.read"
 *   PermReorganisationManage = "org.reorganisation.manage"
 *
 * `positionRead` is deliberately NOT a new string here: `GET
 * /foundation/positions/:id/headcount` is gated server-side on
 * `PermPositionRead` (`org.position.read`) — the existing position-read grant,
 * not a new headcount-specific one — because reading headcount is reading a
 * fact about a position, and `internal/org` already owns that permission. This
 * module imports `ORG_PERMISSIONS.positionRead` rather than redeclaring the
 * literal, so the two files can never drift onto two different strings for the
 * same server check.
 *
 * There is no read-only headcount-*event* permission: raising a requisition,
 * override or reduction is a single governed write behind
 * `PermHeadcountManage` (FSD 1.2.5.11's "named approver who is not the
 * requester" segregation is enforced inside that one write, not by splitting
 * the permission).
 */

import { ORG_PERMISSIONS } from '@/modules/org/permissions'

export const WORKFORCE_PERMISSIONS = {
  namingTemplateRead: 'org.naming_template.read',
  namingTemplateManage: 'org.naming_template.manage',

  headcountManage: 'org.headcount.manage',
  /** Reused from `internal/org` — see file header. */
  positionRead: ORG_PERMISSIONS.positionRead,

  reorganisationRead: 'org.reorganisation.read',
  reorganisationManage: 'org.reorganisation.manage',
} as const

export type WorkforcePermission = (typeof WORKFORCE_PERMISSIONS)[keyof typeof WORKFORCE_PERMISSIONS]
